// All configured list cache instances live here so invalidation points and
// logout can reach them from one import.

import { createListCache } from './listCacheStore';

export const USER_LIST_MAX_QUERIES = 20;
export const USER_LIST_MAX_ROWS_PER_QUERY = 1000;

// In-memory only: user rows are PII and pages are large.
export const userListCache = createListCache({
  namespace: 'userList',
  maxEntries: USER_LIST_MAX_QUERIES,
});

// Profile payloads for the coach's own clients, so paging through the roster
// in the Users split view doesn't refetch a profile per click. In-memory only
// for the same reason as userListCache — profiles are PII — which also gives
// the intended lifetime: the cache survives navigation within the session and
// is dropped by the list's Refresh button, by logout (clearAllListCaches), and
// by a page reload.
export const MY_USER_PROFILE_MAX_ENTRIES = 60;

export const myUserProfileCache = createListCache({
  namespace: 'myUserProfile',
  maxEntries: MY_USER_PROFILE_MAX_ENTRIES,
});

// Call notes per client, so reopening the notes panel paints instantly and
// revalidates behind. Memory-only for the same reason as the profiles above:
// note bodies are free-text PII, and localStorage persistence silently stops
// past MAX_PERSIST_BYTES, which a full roster of rich-text notes would exceed.
export const userNotesCache = createListCache({
  namespace: 'userNotes',
  maxEntries: MY_USER_PROFILE_MAX_ENTRIES,
});

/**
 * Cache key for one client's profile payload.
 * @param {string} userId
 * @returns {string}
 */
export function serializeUserProfileKey(userId) {
  return `profile:${userId}`;
}

// A client's hormone tracker data (4 summaries + a year of raw logs), so
// reopening the Hormonal Wellness tab paints instantly. Memory-only: cycle and
// symptom logs are health data.
export const hormoneDataCache = createListCache({
  namespace: 'hormoneData',
  maxEntries: 10,
});

/**
 * Cache key for one client's hormone data as of a local day — a new day is a
 * new key, because every summary is evaluated "as of" that day.
 * @param {string} userId
 * @param {string} asOf - `YYYY-MM-DD`
 * @returns {string}
 */
export function serializeHormoneDataKey(userId, asOf) {
  return `hormones:${userId}:${asOf}`;
}

// A client's gut health logs (a year of symptom and bowel rows), so reopening
// the Gut Health tab paints instantly. Memory-only: they are health data.
export const gutDataCache = createListCache({
  namespace: 'gutData',
  maxEntries: 10,
});

/**
 * Cache key for one client's gut logs as of a local day.
 * @param {string} userId
 * @param {string} asOf - `YYYY-MM-DD`
 * @returns {string}
 */
export function serializeGutDataKey(userId, asOf) {
  return `gut:${userId}:${asOf}`;
}

// The Gut Health tab's fibre: the client's fibre target, and diet tracking one
// month per entry. Diet tracking over a year takes ~20 s, so the tab fetches
// only the months on screen. Memory-only, like the logs above.
export const gutFibreCache = createListCache({
  namespace: 'gutFibre',
  maxEntries: 60,
});

/**
 * Cache key for one client's fibre target or one month of fibre tracking.
 * @param {string} userId
 * @param {string} asOf - `YYYY-MM-DD`; the current month grows each day.
 * @param {string} eventTypeId - The diet event type the request was sent with.
 * @param {string} part - `'target'` or a month, `YYYY-MM`.
 * @returns {string}
 */
export function serializeGutFibreKey(userId, asOf, eventTypeId, part) {
  return `fibre:${userId}:${asOf}:${eventTypeId}:${part}`;
}

/**
 * Cache key for one client's call notes.
 * @param {string} userId
 * @returns {string}
 */
export function serializeUserNotesKey(userId) {
  return `notes:${userId}`;
}

// Template summaries: small, non-PII payloads keyed by event type id, so they
// persist across reloads. Logout wipes them via clearAllListCaches().
export const dietTemplatesCache = createListCache({
  namespace: 'dietTemplates',
  maxEntries: 3,
  persist: true,
});

export const workoutTemplatesCache = createListCache({
  namespace: 'workoutTemplates',
  maxEntries: 3,
  persist: true,
});

/**
 * Deterministic key for a user-list query combo (never raw JSON.stringify —
 * field order must be stable).
 * @param {{ search?: string, sortBy?: string, sortOrder?: string, segment?: string }} params
 * @returns {string}
 */
export function serializeUserListKey({ search, sortBy, sortOrder, segment }) {
  return [
    `s=${search || ''}`,
    `sb=${sortBy || ''}`,
    `so=${sortOrder || ''}`,
    `seg=${segment || ''}`,
  ].join('|');
}

// ─────────────────────────────────────────────────────────────
// Support workspace
// ─────────────────────────────────────────────────────────────
// In-memory only: ticket subjects, client names and note bodies are customer
// PII, same reasoning as myUserProfileCache above.
//
// The TTLs here are a staleness ceiling and a memory bound, NOT the freshness
// mechanism — every consumer revalidates in the background on mount. A short
// TTL would work against that: an expired entry is purged on read, dropping
// the pane back to a cold loading state for data it already had.

/** One entry per (view, sort, filters) combination the agent has visited. */
export const supportQueueCache = createListCache({
  namespace: 'supportQueue',
  ttlMs: 10 * 60 * 1000,
  maxEntries: 12,
});

/** The saved-view + department chip counts. One key. */
export const supportCountsCache = createListCache({
  namespace: 'supportCounts',
  ttlMs: 5 * 60 * 1000,
  maxEntries: 2,
});

/**
 * Desk-wide reference data — tags, macros per department, agent workload.
 * Shared so the several components that each need it issue one request
 * between them rather than one apiece.
 */
export const supportVocabCache = createListCache({
  namespace: 'supportVocab',
  ttlMs: 30 * 60 * 1000,
  maxEntries: 12,
});

/** Per-ticket and per-requester detail: activity trails, client histories. */
export const supportTicketCache = createListCache({
  namespace: 'supportTicket',
  ttlMs: 10 * 60 * 1000,
  maxEntries: 30,
});

// ─────────────────────────────────────────────────────────────
// Call Booking — coach slots
// ─────────────────────────────────────────────────────────────
// One entry per coach. Shifts are weekly and repeat, so there is no month or
// week in the key — every week of a coach's calendar is the same one list.
// In-memory only; the TTL is a staleness ceiling rather than the freshness
// mechanism — every mount revalidates, and a shift write refreshes outright.
export const coachShiftsCache = createListCache({
  namespace: 'coachShifts',
  ttlMs: 10 * 60 * 1000,
  maxEntries: 24,
});

/**
 * Cache key for one coach's weekly shifts, or `'all'` for every coach's (the
 * roster's one-request read).
 *
 * @param {string} employeeId
 * @returns {string}
 */
export function serializeCoachShiftsKey(employeeId) {
  return `e=${employeeId || 'all'}`;
}

// Every department's call types, in one entry: `/fetch-shift-call-type` is
// token-only and returns the same list to every caller, and it is a handful of
// rows, so there is nothing worth keying by.
export const shiftCallTypesCache = createListCache({
  namespace: 'shiftCallTypes',
  ttlMs: 10 * 60 * 1000,
  maxEntries: 1,
});

export const SHIFT_CALL_TYPES_KEY = 'all';

// ─────────────────────────────────────────────────────────────
// Call Booking — booking records
// ─────────────────────────────────────────────────────────────
// One entry per (coach, month), matching coachSlotsCache above so the bookings
// dashboard and the My-slots conflict check share a single fetch per month.
// In-memory only, and for a stronger reason than the slots cache: these rows
// carry client names and free-text booking reasons.
export const bookingRecordsCache = createListCache({
  namespace: 'bookingRecords',
  ttlMs: 5 * 60 * 1000,
  maxEntries: 12,
});

/**
 * Cache key for one coach's booking records in one month.
 *
 * Keyed by the employee id even though the caller's own fetch is deliberately
 * unfiltered — the rows returned are the ones that caller is entitled to see,
 * so they must not be shared across accounts.
 *
 * `scope` separates the two request shapes that both key on an employee id: the
 * caller's own unfiltered month (`'own'`) and a manager reading one coach's
 * month with `employee_id` set (`'coach'`). Same id, different row sets — one
 * key would let whichever landed first answer for both.
 *
 * @param {string} employeeId
 * @param {number} year
 * @param {number} month - 0-indexed, matching Date.
 * @param {'own'|'coach'} [scope='own']
 * @returns {string}
 */
export function serializeBookingRecordsKey(employeeId, year, month, scope = 'own') {
  return `e=${employeeId}|s=${scope}|m=${year}-${String(month + 1).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────
// Call Booking — the caller's reporting tree
// ─────────────────────────────────────────────────────────────
// `/fetch-employee-hierarchy` is what decides which of the three Bookings
// screens a caller gets, so every employee pays for it on every visit to
// answer a question that comes back empty for most of them. This is what stops
// that being once per navigation.
//
// In-memory only: the payload carries names, emails and role history.
//
// Keyed by the caller's employee_id, and that is not optional — unlike
// `/fetch-employees`, this endpoint *is* caller-scoped, so one shared key would
// let whoever loaded /bookings first hand their own org chart to the next
// account in the same tab.
//
// Short TTL because a manager link created on /hr/employees has to show up here without
// demanding a reload; the roster's own refresh bypasses the cache outright, so
// this is a staleness ceiling rather than the freshness mechanism.
export const employeeHierarchyCache = createListCache({
  namespace: 'employeeHierarchy',
  ttlMs: 2 * 60 * 1000,
  maxEntries: 4,
});

/** One entry per caller — the endpoint takes no other parameter. */
export function serializeEmployeeHierarchyKey(employeeId) {
  return `e=${employeeId}`;
}

// ─────────────────────────────────────────────────────────────
// `/fetch-employees` — the whole tenant, which the Bookings executive roster
// reads because the reporting tree cannot answer "every coach" (an executive
// who manages nobody has an empty tree).
//
// In-memory only, for the same reason as the hierarchy above: names, emails
// and role history.
//
// **One shared key, deliberately** — and this is the opposite of the hierarchy
// cache's rule. That endpoint is caller-scoped, so a shared key would hand one
// account's org chart to the next. This one takes no parameters at all and
// applies no ownership gate: every caller in the tenant gets the identical
// payload, so there is nothing to key by.
//
// Same short TTL: a role assigned on /hr/employees has to reach this table without a
// reload, and the roster's own refresh bypasses the cache outright.
export const employeeListCache = createListCache({
  namespace: 'employeeList',
  ttlMs: 2 * 60 * 1000,
  maxEntries: 1,
});

/**
 * Deterministic key for one queue fetch. Never raw JSON.stringify — key order
 * must be stable or the same query caches under two keys.
 *
 * @param {{ view: string, sort: string, filters: Object }} params
 * @returns {string}
 */
export function serializeSupportQueueKey({ view, sort, filters }) {
  const active = Object.entries(filters || {})
    .filter(([, value]) => value !== '' && value != null)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join(',');
  return `v=${view}|s=${sort}|f=${active}`;
}
